import { useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, User as UserIcon, UserPlus, Users, Copy } from "lucide-react";

/** NTN internal staff management — invite / role change / deactivate /
 *  reactivate / remove. Staff-only surface; server enforces authorization
 *  (require_account_admin + PM 410 hard-block in routes/team.py). */
export default function StaffTeamPanel({ staff, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "ntn_staff" && user?.account_role === "admin";
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-8" data-testid="staff-team-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0A192F]" />
            <h2 className="font-serif text-2xl text-[#0A192F]">NTN staff</h2>
          </div>
          <p className="text-sm text-[#4A5568] mt-1.5 max-w-xl">
            Invite analysts and admins, manage roles, and control account status.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setInviteOpen(true)} className="rounded-sm bg-[#0A192F] hover:bg-[#132a4d]" data-testid="invite-staff-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Invite staff
          </Button>
        )}
      </div>

      <div className="border border-[#0A192F]/10 rounded-sm divide-y divide-[#0A192F]/8">
        {staff.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#4A5568]" data-testid="staff-empty">No staff users yet.</div>
        ) : staff.map((u) => (
          <StaffRow key={u.id} u={u} self={user} isAdmin={isAdmin} onChanged={onChanged} />
        ))}
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onChanged={onChanged} />
    </div>
  );
}

function statusOf(u) {
  if (u.disabled_at) return { label: "Deactivated", cls: "bg-red-50 text-red-700 border-red-200" };
  if (!u.last_login_at) return { label: "Invited", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

function StaffRow({ u, self, isAdmin, onChanged }) {
  const isSelf = u.id === self?.id;
  const s = statusOf(u);
  const [busy, setBusy] = useState(false);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      await apiClient.patch(`/team/users/${u.id}`, body);
      toast.success(okMsg);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from NTN staff?`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`/team/users/${u.id}`);
      toast.success("Staff member removed");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const canManage = isAdmin && !isSelf && !u.is_master;

  return (
    <div className="px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`staff-row-${u.id}`}>
      <div className="h-10 w-10 rounded-full bg-[#F3EFEA] flex items-center justify-center shrink-0">
        {u.account_role === "admin" ? <Shield className="h-4 w-4 text-[#0A192F]" /> : <UserIcon className="h-4 w-4 text-[#4A5568]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0A192F] truncate">
          {u.name} {isSelf && <span className="text-xs text-[#4A5568] font-normal">(you)</span>}
          {u.is_master && <Badge variant="outline" className="ml-2 rounded-sm text-[9px] uppercase font-mono border-[#0A192F]/30" data-testid={`master-badge-${u.id}`}>Master</Badge>}
        </div>
        <div className="text-xs text-[#4A5568] font-mono truncate">{u.email}</div>
      </div>
      <span className={`text-[10px] font-mono uppercase tracking-wide border px-2 py-0.5 rounded-sm ${s.cls}`} data-testid={`staff-status-${u.id}`}>
        {s.label}
      </span>
      {canManage ? (
        <div className="flex items-center gap-2">
          <Select
            value={u.account_role}
            onValueChange={(v) => patch({ account_role: v }, `Role changed to ${v}`)}
            disabled={busy}
          >
            <SelectTrigger className="h-8 w-[110px] rounded-sm text-xs" data-testid={`staff-role-select-${u.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">member</SelectItem>
              <SelectItem value="admin">admin</SelectItem>
            </SelectContent>
          </Select>
          {u.disabled_at ? (
            <Button variant="outline" size="sm" disabled={busy} className="rounded-sm h-8 text-xs"
                    onClick={() => patch({ disabled: false }, "Account reactivated")}
                    data-testid={`staff-reactivate-${u.id}`}>
              Reactivate
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled={busy} className="rounded-sm h-8 text-xs"
                    onClick={() => patch({ disabled: true }, "Account deactivated")}
                    data-testid={`staff-deactivate-${u.id}`}>
              Deactivate
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={busy}
                  className="rounded-sm h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
                  onClick={remove} data-testid={`staff-remove-${u.id}`}>
            Remove
          </Button>
        </div>
      ) : (
        <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#4A5568]">{u.account_role}</span>
      )}
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onChanged }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [method, setMethod] = useState("email_link");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setName(""); setEmail(""); setRole("member"); setMethod("email_link"); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post("/team/users", {
        name, email, account_role: role, invite_method: method,
      });
      setResult(data);
      toast.success(`${name} invited`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="invite-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A192F]">Invite staff member</DialogTitle>
          <DialogDescription>
            New staff sign in with email + password and mandatory email MFA.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="invite-success">
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
              {result.user.name} ({result.user.email}) has been added as <strong>{result.user.account_role}</strong>.
            </div>
            {result.invite_method === "email_link" ? (
              <p className="text-sm text-[#4A5568]">
                An invitation email with a secure set-your-password link (valid 72 hours) is on its way.
              </p>
            ) : (
              <div>
                <p className="text-sm text-[#4A5568] mb-2">
                  Temporary password — shown once, share it securely:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#F3EFEA] border border-[#0A192F]/10 rounded-sm px-3 py-2 font-mono text-sm" data-testid="invite-temp-password">
                    {result.temp_password}
                  </code>
                  <Button variant="outline" size="sm" className="rounded-sm"
                          onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success("Copied"); }}
                          data-testid="invite-copy-password">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => { onOpenChange(false); reset(); }} className="rounded-sm bg-[#0A192F] hover:bg-[#132a4d]" data-testid="invite-done">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="invite-staff-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#4A5568]">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required
                     className="mt-1.5 h-10 rounded-sm" placeholder="Jane Analyst" data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#4A5568]">Work email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                     className="mt-1.5 h-10 rounded-sm" placeholder="jane@ntnverify.com" data-testid="invite-email" />
              <p className="text-xs text-[#4A5568] mt-1">Must be a real inbox — sign-in requires an emailed MFA code.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#4A5568]">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5 h-10 rounded-sm" data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member — analyst: Operations, screenings, reports</SelectItem>
                  <SelectItem value="admin">Admin — everything + staff &amp; client administration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#4A5568]">Invite method</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                <button type="button" onClick={() => setMethod("email_link")}
                        className={`text-left border rounded-sm px-3 py-2.5 text-sm transition-colors ${method === "email_link" ? "border-[#0A192F] bg-[#F3EFEA]" : "border-[#0A192F]/15 hover:border-[#0A192F]/40"}`}
                        data-testid="invite-method-email">
                  <span className="font-medium text-[#0A192F] block">Email link</span>
                  <span className="text-xs text-[#4A5568]">They set their own password</span>
                </button>
                <button type="button" onClick={() => setMethod("temp_password")}
                        className={`text-left border rounded-sm px-3 py-2.5 text-sm transition-colors ${method === "temp_password" ? "border-[#0A192F] bg-[#F3EFEA]" : "border-[#0A192F]/15 hover:border-[#0A192F]/40"}`}
                        data-testid="invite-method-temp">
                  <span className="font-medium text-[#0A192F] block">Temporary password</span>
                  <span className="text-xs text-[#4A5568]">Shown once, share securely</span>
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy || !name || !email}
                      className="rounded-sm bg-[#0A192F] hover:bg-[#132a4d]" data-testid="invite-submit">
                {busy ? "Inviting…" : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
